import { lazy, Suspense } from 'react';
import { Route, Routes } from 'react-router-dom';
import { Center, Loader } from '@mantine/core';

import { SiteLayout } from '@/components/layout/SiteLayout';
import { ScrollToTop } from '@/components/layout/ScrollToTop';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { LandingPage } from '@/pages/LandingPage';

// Code-split the non-landing routes — landing is the showpiece and ships eagerly.
const DownloadPage = lazy(() =>
  import('@/pages/DownloadPage').then((m) => ({ default: m.DownloadPage })),
);
const PricingPage = lazy(() =>
  import('@/pages/PricingPage').then((m) => ({ default: m.PricingPage })),
);
const AboutPage = lazy(() =>
  import('@/pages/AboutPage').then((m) => ({ default: m.AboutPage })),
);
const PrivacyPage = lazy(() =>
  import('@/pages/PrivacyPage').then((m) => ({ default: m.PrivacyPage })),
);
const TermsPage = lazy(() =>
  import('@/pages/TermsPage').then((m) => ({ default: m.TermsPage })),
);
const BlogPage = lazy(() =>
  import('@/pages/BlogPage').then((m) => ({ default: m.BlogPage })),
);
const CommunityPage = lazy(() =>
  import('@/pages/CommunityPage').then((m) => ({ default: m.CommunityPage })),
);
const CategoryPage = lazy(() =>
  import('@/pages/CategoryPage').then((m) => ({ default: m.CategoryPage })),
);
const ThreadPage = lazy(() =>
  import('@/pages/ThreadPage').then((m) => ({ default: m.ThreadPage })),
);
const NewThreadPage = lazy(() =>
  import('@/pages/NewThreadPage').then((m) => ({ default: m.NewThreadPage })),
);
const TuneLibraryPage = lazy(() =>
  import('@/pages/TuneLibraryPage').then((m) => ({ default: m.TuneLibraryPage })),
);
const TuneDetailPage = lazy(() =>
  import('@/pages/TuneDetailPage').then((m) => ({ default: m.TuneDetailPage })),
);
const PlatformDetailPage = lazy(() =>
  import('@/pages/PlatformDetailPage').then((m) => ({ default: m.PlatformDetailPage })),
);
const AnalyzerPage = lazy(() =>
  import('@/pages/AnalyzerPage').then((m) => ({ default: m.AnalyzerPage })),
);
const NotFoundPage = lazy(() =>
  import('@/pages/NotFoundPage').then((m) => ({ default: m.NotFoundPage })),
);
const LoginPage = lazy(() =>
  import('@/pages/LoginPage').then((m) => ({ default: m.LoginPage })),
);
const RegisterPage = lazy(() =>
  import('@/pages/RegisterPage').then((m) => ({ default: m.RegisterPage })),
);
const ForgotPasswordPage = lazy(() =>
  import('@/pages/ForgotPasswordPage').then((m) => ({ default: m.ForgotPasswordPage })),
);
const ResetPasswordPage = lazy(() =>
  import('@/pages/ResetPasswordPage').then((m) => ({ default: m.ResetPasswordPage })),
);
const ProfilePage = lazy(() =>
  import('@/pages/ProfilePage').then((m) => ({ default: m.ProfilePage })),
);
const ProfileSettingsPage = lazy(() =>
  import('@/pages/ProfileSettingsPage').then((m) => ({ default: m.ProfileSettingsPage })),
);
const GarageSettingsPage = lazy(() =>
  import('@/pages/GarageSettingsPage').then((m) => ({ default: m.GarageSettingsPage })),
);

function RouteFallback() {
  return (
    <Center mih="60vh" role="status" aria-label="Loading page">
      <Loader color="forge" size="md" />
    </Center>
  );
}

export function App() {
  return (
    <>
      <ScrollToTop />
      <Routes>
        <Route element={<SiteLayout />}>
          <Route index element={<LandingPage />} />
          <Route
            path="download"
            element={
              <Suspense fallback={<RouteFallback />}>
                <DownloadPage />
              </Suspense>
            }
          />
          <Route
            path="pricing"
            element={
              <Suspense fallback={<RouteFallback />}>
                <PricingPage />
              </Suspense>
            }
          />
          <Route
            path="about"
            element={
              <Suspense fallback={<RouteFallback />}>
                <AboutPage />
              </Suspense>
            }
          />
          <Route
            path="privacy"
            element={
              <Suspense fallback={<RouteFallback />}>
                <PrivacyPage />
              </Suspense>
            }
          />
          <Route
            path="terms"
            element={
              <Suspense fallback={<RouteFallback />}>
                <TermsPage />
              </Suspense>
            }
          />
          <Route
            path="blog"
            element={
              <Suspense fallback={<RouteFallback />}>
                <BlogPage />
              </Suspense>
            }
          />
          <Route
            path="community"
            element={
              <Suspense fallback={<RouteFallback />}>
                <CommunityPage />
              </Suspense>
            }
          />
          <Route
            path="community/category/:slug"
            element={
              <Suspense fallback={<RouteFallback />}>
                <CategoryPage />
              </Suspense>
            }
          />
          <Route
            path="community/thread/:id"
            element={
              <Suspense fallback={<RouteFallback />}>
                <ThreadPage />
              </Suspense>
            }
          />
          <Route
            path="community/new-thread"
            element={
              <Suspense fallback={<RouteFallback />}>
                <ProtectedRoute>
                  <NewThreadPage />
                </ProtectedRoute>
              </Suspense>
            }
          />
          <Route
            path="community/tunes"
            element={
              <Suspense fallback={<RouteFallback />}>
                <TuneLibraryPage />
              </Suspense>
            }
          />
          <Route
            path="community/tunes/:slug"
            element={
              <Suspense fallback={<RouteFallback />}>
                <TuneDetailPage />
              </Suspense>
            }
          />
          <Route
            path="platforms/:slug"
            element={
              <Suspense fallback={<RouteFallback />}>
                <PlatformDetailPage />
              </Suspense>
            }
          />
          <Route
            path="analyzer"
            element={
              <Suspense fallback={<RouteFallback />}>
                <AnalyzerPage />
              </Suspense>
            }
          />
          <Route
            path="login"
            element={
              <Suspense fallback={<RouteFallback />}>
                <LoginPage />
              </Suspense>
            }
          />
          <Route
            path="register"
            element={
              <Suspense fallback={<RouteFallback />}>
                <RegisterPage />
              </Suspense>
            }
          />
          <Route
            path="forgot-password"
            element={
              <Suspense fallback={<RouteFallback />}>
                <ForgotPasswordPage />
              </Suspense>
            }
          />
          <Route
            path="reset-password/:token"
            element={
              <Suspense fallback={<RouteFallback />}>
                <ResetPasswordPage />
              </Suspense>
            }
          />
          <Route
            path="profile/:username"
            element={
              <Suspense fallback={<RouteFallback />}>
                <ProfilePage />
              </Suspense>
            }
          />
          <Route
            path="settings/profile"
            element={
              <Suspense fallback={<RouteFallback />}>
                <ProtectedRoute>
                  <ProfileSettingsPage />
                </ProtectedRoute>
              </Suspense>
            }
          />
          <Route
            path="settings/garage"
            element={
              <Suspense fallback={<RouteFallback />}>
                <ProtectedRoute>
                  <GarageSettingsPage />
                </ProtectedRoute>
              </Suspense>
            }
          />
          <Route
            path="*"
            element={
              <Suspense fallback={<RouteFallback />}>
                <NotFoundPage />
              </Suspense>
            }
          />
        </Route>
      </Routes>
    </>
  );
}
